"use client"

import { useState, useMemo, useEffect, useRef, useDeferredValue } from "react"
import Image from "next/image"
import Link from "next/link"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { CATEGORIES, type ArticleData } from "@/data/articles"
import { track } from "@/lib/analytics"
import { useCountry } from "@/components/layout/country-provider"
import { isMembershipFirstSite } from "@/lib/membership-first"
import { getBcp47Locale } from "@/i18n/config"
import { BookingCta } from "@/components/ui/booking-cta"
import { PlanSiteClose } from "@/app/(frontend)/[country]/[locale]/_components/membership-first/pricing/pricing-v5-client"

/* ───────────────────── Helpers ───────────────────── */

function formatDate(dateStr: string, bcp47Locale: string): string {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  return date.toLocaleDateString(bcp47Locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  })
}

const CARD_BG: Record<ArticleData["cardColor"], string> = {
  aubergine: "bg-aubergine-900",
  olive: "bg-light-green-900",
  lavender: "bg-[#6B5B8A]",
  white: "bg-white",
}

function isLightCard(color: ArticleData["cardColor"]): boolean {
  return color === "white"
}

/* ───────────────────── Article card ───────────────────── */

function ArticleCard({
  article,
  locale,
  categoryLabels,
  mf = false,
}: {
  article: ArticleData
  index?: number
  locale: string
  categoryLabels: Record<string, string>
  /** The membership-first site: a white card on the site's type scale, as
      its test cards are, not the coloured plates (Eugene, 12.10). */
  mf?: boolean
}) {
  const light = mf || isLightCard(article.cardColor)
  const lp = useLocalePath()

  return (
    <Link
      href={lp(`/insights/${article.slug}`)}
      className={
        mf
          ? "block overflow-hidden rounded-[20px] bg-white transition-transform hover:-translate-y-1"
          : `block overflow-hidden rounded-[16px] transition-transform hover:-translate-y-1 ${CARD_BG[article.cardColor]}`
      }
    >
      {/* Image */}
      <div className="relative">
        {article.heroImage ? (
          <div className="relative aspect-[16/10] w-full">
            <Image
              src={article.heroImage}
              alt={article.title}
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              className="rounded-t-[12px] object-cover"
            />
          </div>
        ) : (
          <div className="flex aspect-[16/10] w-full items-center justify-center rounded-t-[12px] bg-neutral-200">
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              className="text-neutral-400"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="m21 15-5-5L5 21" />
            </svg>
          </div>
        )}
        <span
          className={
            mf
              ? "type-label-md absolute top-3 left-3 rounded-full bg-white/90 px-3 py-1 text-neutral-700 uppercase backdrop-blur-sm"
              : "type-body-sm absolute top-3 left-3 rounded-full bg-white/90 px-3 py-1 font-mono font-medium tracking-[0.15em] text-neutral-700 uppercase backdrop-blur-sm"
          }
        >
          {categoryLabels[article.category] || article.categoryLabel}
        </span>
      </div>

      {/* Content */}
      <div className={mf ? "px-6 pt-5 pb-6" : "px-5 pt-5 pb-5"}>
        {mf ? (
          <h3 className="type-headline-sm text-neutral-900">{article.title}</h3>
        ) : (
          <h3
            className={`font-normal ${light ? "text-foreground" : "text-white"}`}
            style={{ fontSize: 22, lineHeight: "22px" }}
          >
            {article.title}
          </h3>
        )}
        <p
          className={
            mf
              ? "type-body-lg mt-2 line-clamp-2 text-neutral-500"
              : `mt-2 line-clamp-2 text-sm ${light ? "text-neutral-500" : "text-white/70"}`
          }
        >
          {article.excerpt}
        </p>

        {/* Author row */}
        <div className="mt-4 flex items-center gap-2.5">
          {article.authorPhoto && (
            <Image
              src={article.authorPhoto}
              alt={article.author}
              width={32}
              height={32}
              className="h-8 w-8 rounded-full object-cover"
            />
          )}
          <div className="flex items-center gap-2">
            <span
              className={`text-sm ${mf ? "text-neutral-900" : light ? "text-aubergine-900" : "text-white"}`}
            >
              {article.author}
            </span>
            <span
              className={`text-xs ${light ? "text-neutral-400" : "text-white/50"}`}
            >
              {formatDate(article.date, locale)}
            </span>
          </div>
        </div>
      </div>
    </Link>
  )
}

/* ───────────────────── Page ───────────────────── */

export default function InsightsClient({
  initialArticles,
}: {
  initialArticles: ArticleData[]
}) {
  const t = useTranslations("insights")
  const ut = useTranslations("ui")
  const locale = useLocale()
  const { code: country } = useCountry()
  const mf = isMembershipFirstSite(country)
  const bcp47 = getBcp47Locale(locale, country)
  const [activeCategory, setActiveCategory] = useState<string>("all")
  const [search, setSearch] = useState("")
  const deferredSearch = useDeferredValue(search)
  const [articles] = useState<ArticleData[]>(initialArticles)

  const categoryLabels: Record<string, string> = useMemo(
    () => ({
      "early-detection": t("earlyDetection"),
      technology: t("technology"),
      "nutrition-lifestyle": t("nutritionLifestyle"),
      "cost-insurance": t("costInsurance"),
      news: t("news"),
    }),
    [t]
  )

  const filteredArticles = useMemo(() => {
    let results = articles

    if (activeCategory !== "all") {
      results = results.filter((a) => a.category === activeCategory)
    }

    if (deferredSearch.trim()) {
      const q = deferredSearch.toLowerCase()
      results = results.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q)
      )
    }

    return results
  }, [activeCategory, deferredSearch, articles])

  // PostHog `search_query` — debounced + deduplicated + min-length so
  // single-character keystrokes don't drown the signal in noise.
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const lastTrackedRef = useRef<{ query: string; count: number } | null>(null)
  useEffect(() => {
    clearTimeout(searchTimerRef.current)
    const trimmed = search.trim()
    if (trimmed.length < 3) return
    searchTimerRef.current = setTimeout(() => {
      const last = lastTrackedRef.current
      if (
        last &&
        last.query === trimmed &&
        last.count === filteredArticles.length
      ) {
        return
      }
      lastTrackedRef.current = {
        query: trimmed,
        count: filteredArticles.length,
      }
      track.searchQuery({
        query: trimmed,
        result_count: filteredArticles.length,
        surface: "insights",
        locale,
        zero_results: filteredArticles.length === 0,
      })
    }, 1200)
    return () => clearTimeout(searchTimerRef.current)
  }, [search, filteredArticles.length, locale])

  // Only offer pills for categories at least one visible article uses — CMS
  // `hideInCountries` curation (e.g. NL's reviewed subset) can leave a
  // category empty, and an empty-filter pill is a dead end. With fewer than
  // two non-empty categories the row filters nothing, so it collapses.
  const tabs = useMemo(() => {
    const present = new Set(articles.map((a) => a.category))
    const categoryTabs = CATEGORIES.filter((c) => present.has(c.id)).map(
      (c) => ({
        id: c.id as string,
        label: categoryLabels[c.id] || c.label,
      })
    )
    if (categoryTabs.length < 2) return []
    return [{ id: "all", label: t("all") }, ...categoryTabs]
  }, [articles, categoryLabels, t])

  return (
    <>
      {/* Hero */}
      <section className="pt-12 pb-4 md:pt-20">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <p
            className={
              mf
                ? "type-label-lg text-neutral-500 uppercase"
                : "type-body-sm font-mono font-medium tracking-[0.15em] text-neutral-500 uppercase"
            }
          >
            {t("pageLabel").toUpperCase()}
          </p>
          {/* The membership-first site puts the heading on the DS display rung. */}
          {mf ? (
            <h1 className="type-display-lg mt-3 text-neutral-900">
              {t("heading")}
            </h1>
          ) : (
            <h1
              className="text-foreground mt-3 font-normal"
              style={{ fontSize: "clamp(36px, 5vw, 56px)", lineHeight: 0.9 }}
            >
              {t("heading")}
            </h1>
          )}
          <p
            className={`type-body-lg mt-4 max-w-2xl ${mf ? "md:type-body-xl text-neutral-500" : "text-neutral-600"}`}
          >
            {t("subtitle")}
          </p>

          {/* Filter row */}
          <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Tabs */}
            {tabs.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveCategory(tab.id)}
                    // no-raw-ids: tab.id only picks the active style, the tab renders its label
                    className={`cursor-pointer transition-all ${mf ? "type-label-lg rounded-full px-4 py-2 uppercase" : "type-label-md rounded-lg px-3 py-2 font-mono font-medium uppercase"} ${
                      activeCategory === tab.id
                        ? "bg-foreground text-white"
                        : "text-foreground bg-white hover:bg-neutral-50"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Search */}
            <div className="relative w-full md:ml-auto md:w-64">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="absolute top-1/2 left-3 -translate-y-1/2 text-neutral-400"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder={ut("searchArticles")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-full border border-neutral-200 bg-white py-2 pr-4 pl-10 text-sm text-neutral-700 transition-colors placeholder:text-neutral-400 focus-visible:border-neutral-400 focus-visible:outline-none"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Article grid */}
      <section className="pb-20">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          {filteredArticles.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredArticles.map((article, index) => (
                <ArticleCard
                  key={article.slug}
                  article={article}
                  index={index}
                  locale={bcp47}
                  categoryLabels={categoryLabels}
                  mf={mf}
                />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center">
              <p className="text-lg text-neutral-500">{t("noPostsFound")}</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA. The membership-first site closes on its plan, as every other
          page of it does. */}
      {mf ? (
        <PlanSiteClose trackingLocation="insights" />
      ) : (
        <section className="pb-20">
          <div className="mx-auto max-w-[1440px] px-4 md:px-10">
            <div className="bg-aubergine-900 flex flex-col items-start gap-8 overflow-hidden rounded-[16px] p-8 md:flex-row md:items-center md:gap-16 md:p-14">
              <div className="flex-1">
                <h2 className="text-3xl leading-snug font-normal text-white md:text-4xl">
                  {t("ctaHeading")}
                </h2>
                <p className="mt-4 max-w-lg text-sm leading-relaxed text-white/70">
                  {t("ctaBody")}
                </p>
              </div>
              <BookingCta
                trackingLabel="Book check-up"
                trackingLocation="insights"
                className="text-aubergine-900 inline-flex flex-shrink-0 items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-medium transition-all hover:bg-neutral-100"
              >
                {t("ctaButton")}
              </BookingCta>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
    </>
  )
}
